import React from 'react';
import { formatCurrency } from '../../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Badge } from '../../ui/badge';
import {
  TrendingUp, DoorOpen, Clock, AlertCircle, UserCheck, UserX, PieChart as PieChartIcon
} from 'lucide-react';
import {
  Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, Legend, ComposedChart
} from 'recharts';

export const ReportsOverviewTab = ({ monthlyBreakdown, financialSummary, tenants, payments, properties }) => {
  const incomeExpenseData = [];
  const incomeMap = {};
  const expenseMap = {};

  monthlyBreakdown?.income?.forEach(item => {
    incomeMap[item.month || item._id] = item.total;
  });
  monthlyBreakdown?.expenses?.forEach(item => {
    expenseMap[item.month || item._id] = item.total;
  });

  const allMonths = [...new Set([...Object.keys(incomeMap), ...Object.keys(expenseMap)])].sort();
  allMonths.forEach(month => {
    incomeExpenseData.push({
      month: month,
      income: incomeMap[month] || 0,
      expenses: expenseMap[month] || 0,
      net: (incomeMap[month] || 0) - (expenseMap[month] || 0)
    });
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Income vs Expenses Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            Income vs Expenses
          </CardTitle>
          <CardDescription>Monthly comparison over time</CardDescription>
        </CardHeader>
        <CardContent>
          {incomeExpenseData.length > 0 ? (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={incomeExpenseData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8DCC8" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `₱${v/1000}k`} tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(value, name) => [formatCurrency(value), name.charAt(0).toUpperCase() + name.slice(1)]}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E8DCC8' }}
                  />
                  <Legend />
                  <Bar dataKey="income" fill="#6B8E6B" name="Income" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expenses" fill="#C85A42" name="Expenses" radius={[4, 4, 0, 0]} />
                  <Line type="monotone" dataKey="net" stroke="#2F4F4F" strokeWidth={2} name="Net" dot={{ r: 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              No data available yet
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChartIcon className="w-5 h-5 text-[#C85A42]" />
            Quick Statistics
          </CardTitle>
          <CardDescription>At-a-glance metrics</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-emerald-50 dark:bg-emerald-950 rounded-lg">
              <div className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                <span className="font-medium text-emerald-900 dark:text-emerald-100">Active Tenants</span>
              </div>
              <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">{tenants?.total_active || 0}</Badge>
            </div>

            <div className="flex justify-between items-center p-3 bg-amber-50 dark:bg-amber-950 rounded-lg">
              <div className="flex items-center gap-2">
                <UserX className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                <span className="font-medium text-amber-900 dark:text-amber-100">Pending Tenants</span>
              </div>
              <Badge variant="secondary" className="bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">{tenants?.total_pending || 0}</Badge>
            </div>

            <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
              <div className="flex items-center gap-2">
                <DoorOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <span className="font-medium text-blue-900 dark:text-blue-100">Vacant Units</span>
              </div>
              <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">{properties?.occupancy?.reduce((sum, p) => sum + p.vacant, 0) || 0}</Badge>
            </div>

            <div className="flex justify-between items-center p-3 bg-purple-50 dark:bg-purple-950 rounded-lg">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <span className="font-medium text-purple-900 dark:text-purple-100">Pending Verification</span>
              </div>
              <Badge variant="secondary" className="bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">{payments?.pending?.length || 0}</Badge>
            </div>

            <div className="flex justify-between items-center p-3 bg-red-50 dark:bg-red-950 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                <span className="font-medium text-red-900 dark:text-red-100">Pending Amount</span>
              </div>
              <Badge variant="secondary" className="bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">{formatCurrency(financialSummary?.pending_payments || 0)}</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
